// PLATFORM-UX-001 — Banner de versión (operador) + auto-actualización
// silenciosa (tienda). No intrusivo: pill fija abajo-derecha, solo operador.
// Store: sin banner; recarga automática cuando idle (una vez por versión).
import React, { useEffect, useState } from 'react';
import {
  subscribeVersion, getVersionState, confirmUpdate, dismissVersion,
  VersionState,
} from './versionAwareness';
import { busyRegistry } from './busyRegistry';

export const VersionBanner: React.FC<{ mode: 'operator' | 'store' }> = ({ mode }) => {
  const [state, setState] = useState<VersionState>(getVersionState());
  useEffect(() => subscribeVersion(() => setState(getVersionState())), []);

  // Tienda: actualización silenciosa — detectada la versión nueva y sin
  // operaciones críticas → recargar una sola vez por versión (sessionStorage).
  useEffect(() => {
    if (mode !== 'store' || state.status !== 'new' || !state.newHash) return;
    const key = 'kf25_ver_auto_' + state.newHash;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* noop */ }
    const t = window.setTimeout(() => {
      if (!busyRegistry.isBusy()) confirmUpdate();
    }, 5000);
    return () => window.clearTimeout(t);
  }, [mode, state]);

  if (mode !== 'operator' || state.status === 'current') return null;

  const pending = state.status === 'deferred';
  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-sm">
      <div className="bg-white rounded-xl shadow-2xl border border-purple-200 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {pending ? '🔄 Actualización pendiente…' : '🔄 Una nueva versión está disponible.'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {pending
                ? 'Se aplicará automáticamente al terminar la operación actual.'
                : 'Tu sesión y tu trabajo se conservarán al actualizar.'}
            </p>
          </div>
          <button
            onClick={dismissVersion}
            aria-label="Descartar"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none shrink-0"
          >✕</button>
        </div>
        <button
          onClick={confirmUpdate}
          disabled={pending}
          className="mt-3 w-full py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          {pending ? '⏳ Esperando…' : 'Actualizar ahora'}
        </button>
      </div>
    </div>
  );
};
